import { useState } from "react";
import {
  Button,
  EmptyState,
  FormModal,
  Input,
  Page,
  ResponsiveGrid,
  Select,
  Text,
  Textarea,
} from "@marekl/adminer/components";
import { pluralizeCzech } from "@marekl/adminer/format";
import { useConfirm } from "@marekl/adminer/hooks";
import { notifyError } from "@marekl/adminer/api";
import {
  useCreateDeductionItem,
  useCreateDeductionTable,
  useDeleteDeductionItem,
  useDeleteDeductionTable,
  useUpdateDeductionItem,
  useUpdateDeductionTable,
  type DeductionItem,
  type DeductionTable,
  type DeductionUnit,
  useDuplicateDeductionTable,
} from "../../../lib/queries/rules";
import {
  DEDUCTION_UNIT_LABELS,
  formatDeduction,
  formatPoints,
  parsePointsInput,
} from "../../../lib/rulesLabels";
import { ComponentCard } from "./ComponentCard";
import { reorderRows } from "./reorder";
import tableStyles from "./componentTables.module.css";
import type { ID } from "../../../lib/id";

type ModalState =
  | { kind: "closed" }
  | { kind: "table"; table?: DeductionTable }
  | { kind: "row"; tableId: ID; row?: DeductionItem };

interface RowForm {
  name: string;
  description: string;
  points: string;
  unit: DeductionUnit;
}

const emptyRowForm: RowForm = {
  name: "",
  description: "",
  points: "",
  unit: "occurrence",
};

export function DeductionTablesTab({
  ruleSetId,
  readOnly,
  tables,
}: {
  ruleSetId: ID;
  readOnly: boolean;
  tables: DeductionTable[];
}) {
  const confirm = useConfirm();
  const [modal, setModal] = useState<ModalState>({ kind: "closed" });
  const [tableName, setTableName] = useState("");
  const [rowForm, setRowForm] = useState<RowForm>(emptyRowForm);

  const createTable = useCreateDeductionTable(ruleSetId);
  const updateTable = useUpdateDeductionTable();
  const deleteTable = useDeleteDeductionTable();
  const duplicateTable = useDuplicateDeductionTable();
  const createRow = useCreateDeductionItem();
  const updateRow = useUpdateDeductionItem();
  const deleteRow = useDeleteDeductionItem();

  const close = () => {
    setModal({ kind: "closed" });
    createTable.reset();
    updateTable.reset();
    duplicateTable.reset();
    createRow.reset();
    updateRow.reset();
  };

  const openTable = (table?: DeductionTable) => {
    setTableName(table?.name ?? "");
    createTable.reset();
    updateTable.reset();
    setModal({ kind: "table", table });
  };

  const openRow = (tableId: ID, row?: DeductionItem) => {
    setRowForm(
      row
        ? {
            name: row.name,
            description: row.description,
            points: formatPoints(row.points_cp),
            unit: row.unit,
          }
        : emptyRowForm,
    );
    createRow.reset();
    updateRow.reset();
    setModal({ kind: "row", tableId, row });
  };

  const submitTable = () => {
    if (modal.kind !== "table") return;
    const payload = { name: tableName.trim() };
    if (modal.table) {
      updateTable.mutate({ id: modal.table.id, payload }, { onSuccess: close });
    } else {
      createTable.mutate(payload, { onSuccess: close });
    }
  };

  const submitRow = () => {
    if (modal.kind !== "row") return;
    const pointsCp = parsePointsInput(rowForm.points);
    if (pointsCp === null) return;
    const payload = {
      name: rowForm.name.trim(),
      description: rowForm.description.trim(),
      points_cp: pointsCp,
      unit: rowForm.unit,
      sort_order:
        modal.row?.sort_order ??
        (tables.find((t) => t.id === modal.tableId)?.items ?? []).length,
    };
    if (modal.row) {
      updateRow.mutate({ id: modal.row.id, payload }, { onSuccess: close });
    } else {
      createRow.mutate({ tableId: modal.tableId, payload }, { onSuccess: close });
    }
  };

  const move = (table: DeductionTable, index: number, direction: -1 | 1) => {
    for (const { row, sort_order } of reorderRows(table.items ?? [], index, direction)) {
      updateRow.mutate({
        id: row.id,
        payload: {
          name: row.name,
          description: row.description,
          points_cp: row.points_cp,
          unit: row.unit,
          sort_order,
        },
      }, { onError: (err) => notifyError(err) });
    }
  };

  const rowInvalid = !rowForm.name.trim() || parsePointsInput(rowForm.points) === null;

  return (
    <>
      <Page.SectionHeader
        title="Srážkové tabulky"
        meta={
          readOnly ? undefined : (
            <Button variant="primary" onClick={() => openTable()}>
              Nová tabulka
            </Button>
          )
        }
      />
      <Text variant="muted">
        Srážky se zadávají kladně, znaménko doplní systém. Liší-li se hodnota podle
        velikosti formace, založte druhou tabulku a přiřaďte ji příslušným kombinacím.
      </Text>

      {tables.length === 0 ? (
        <EmptyState
          title="Žádné srážkové tabulky"
          description="Založte tabulku a vypište do ní jednotlivé srážky."
        />
      ) : (
        tables.map((table) => (
          <ComponentCard<DeductionItem>
            key={table.id}
            title={table.name}
            subtitle={<Text variant="muted">{(table.items ?? []).length} {pluralizeCzech((table.items ?? []).length, ["srážka", "srážky", "srážek"])}</Text>}
            readOnly={readOnly}
            rows={table.items ?? []}
            emptyTitle="Zatím bez srážek"
            emptyDescription="Přidejte první srážku."
            addLabel="Přidat srážku"
            columns={[
              {
                key: "name",
                header: "Srážka",
                tdClass: tableStyles.nameOnly,
                render: (r) => (
                  <>
                    <div>{r.name}</div>
                    {r.description && <Text variant="muted">{r.description}</Text>}
                  </>
                ),
              },
              {
                key: "points",
                header: "Hodnota",
                numeric: true,
                tdClass: tableStyles.value,
                render: (r) => <Text variant="mono">{formatDeduction(r.points_cp, r.unit)}</Text>,
              },
              {
                key: "unit",
                header: "Jednotka",
                tdClass: tableStyles.value,
                render: (r) => DEDUCTION_UNIT_LABELS[r.unit],
              },
            ]}
            onEditTable={() => openTable(table)}
            onDuplicateTable={() => duplicateTable.mutate({ id: table.id }, { onError: (err) => notifyError(err) })}
            onDeleteTable={() =>
              confirm({
                title: "Smazat srážkovou tabulku",
                description: (
                  <>
                    Smaže se tabulka <strong>{table.name}</strong> i všechny její srážky.
                    Tabulku, na kterou odkazuje disciplína nebo některá kombinace, smazat nelze.
                  </>
                ),
                confirmLabel: "Smazat",
                variant: "danger",
                onConfirm: () => deleteTable.mutateAsync(table.id),
              })
            }
            onAddRow={() => openRow(table.id)}
            onEditRow={(row) => openRow(table.id, row)}
            onDeleteRow={(row) =>
              confirm({
                title: "Smazat srážku",
                description: (
                  <>
                    Smaže se srážka <strong>{row.name}</strong>.
                  </>
                ),
                confirmLabel: "Smazat",
                variant: "danger",
                onConfirm: () => deleteRow.mutateAsync(row.id),
              })
            }
            onMoveRow={(index, direction) => move(table, index, direction)}
          />
        ))
      )}

      <FormModal
        open={modal.kind === "table"}
        title={modal.kind === "table" && modal.table ? "Upravit tabulku" : "Nová srážková tabulka"}
        onClose={close}
        submitLabel="Uložit"
        busy={createTable.isPending || updateTable.isPending}
        canSubmit={!!tableName.trim()}
        error={createTable.error || updateTable.error}
        onSubmit={submitTable}
      >
        <Input
          label="Název tabulky"
          required
          placeholder="Srážky – sólo a duo/trio"
          value={tableName}
          onChange={(e) => setTableName(e.target.value)}
        />
      </FormModal>

      <FormModal
        open={modal.kind === "row"}
        title={modal.kind === "row" && modal.row ? "Upravit srážku" : "Nová srážka"}
        onClose={close}
        submitLabel="Uložit"
        busy={createRow.isPending || updateRow.isPending}
        canSubmit={!rowInvalid}
        error={createRow.error || updateRow.error}
        onSubmit={submitRow}
      >
        <Input
          label="Název srážky"
          required
          placeholder="Pád náčiní"
          value={rowForm.name}
          onChange={(e) => setRowForm({ ...rowForm, name: e.target.value })}
        />
        <ResponsiveGrid columns={2}>
          <Input
            label="Hodnota (body)"
            required
            placeholder="0,10"
            value={rowForm.points}
            onChange={(e) => setRowForm({ ...rowForm, points: e.target.value })}
            error={
              rowForm.points.trim() !== "" && parsePointsInput(rowForm.points) === null
                ? "Zadejte kladné číslo nejvýše se dvěma desetinnými místy."
                : undefined
            }
            hint="Kladně, například 0,10."
          />
          <Select
            label="Jednotka"
            value={rowForm.unit}
            onChange={(e) => setRowForm({ ...rowForm, unit: e.target.value as DeductionUnit })}
          >
            {Object.entries(DEDUCTION_UNIT_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
        </ResponsiveGrid>
        <Textarea
          label="Popis"
          rows={2}
          value={rowForm.description}
          onChange={(e) => setRowForm({ ...rowForm, description: e.target.value })}
          hint="Nepovinné upřesnění, kdy se srážka uděluje."
        />
      </FormModal>


    </>
  );
}
